import { defaultShouldDehydrateQuery } from "@tanstack/react-query";

/**
 * Query key segments that indicate sensitive or ephemeral data
 * which must not be persisted to IndexedDB.
 */
export const DENIED_QUERY_KEY_SEGMENTS: ReadonlySet<string> = new Set([
    // Short-lived JWT tokens
    "logsToken",
    "accessToken",
    // Webhook signing secrets
    "secret",
    // Pylon chat identity tokens
    "chat-identity-token",
    // Agent execution conversation tokens
    "create-conversation-token",
    // Login providers (session-specific)
    "loginProviders",
    // Supervisor-sourced queries: ephemeral, environment-specific, and
    // potentially very large (100+ changed files with full diffs).
    // Persisting these causes main-thread stalls during structured-clone
    // serialization and bloats the IndexedDB store.
    "gitChanges",
    "gitDiff",
    "gitDiffFiles",
    "fileDiffContent",
    "readFile",
    "directoryListing",
    "findFiles",
]);

/**
 * Determines whether a query should be persisted to IndexedDB.
 *
 * Excludes:
 * - Queries that fail TanStack's default dehydration check (e.g., no data)
 * - Queries with gcTime === 0 (explicitly opted out of caching)
 * - Queries whose keys contain denied segments (tokens, secrets)
 */
// Parameter type uses the Query shape from defaultShouldDehydrateQuery.
// The persist-client may bundle a different query-core copy with a nominally
// distinct Query type, so the call site casts when passing to persistOptions.
export function shouldDehydrateQuery(query: Parameters<typeof defaultShouldDehydrateQuery>[0]): boolean {
    if (!defaultShouldDehydrateQuery(query)) {
        return false;
    }

    // Queries with gcTime === 0 explicitly opt out of caching
    if (query.gcTime === 0) {
        return false;
    }

    // Check if any segment of the query key matches the denylist
    return !queryKeyContainsDeniedSegment(query.queryKey);
}

function queryKeyContainsDeniedSegment(queryKey: readonly unknown[]): boolean {
    for (const segment of queryKey) {
        if (typeof segment === "string" && DENIED_QUERY_KEY_SEGMENTS.has(segment)) {
            return true;
        }
    }
    return false;
}
